import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileToBase64, CATEGORIES, SEASONS, FORMALITIES, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Sparkles, Check, X, Loader2, AlertTriangle, Scan, ArrowRight, ScanLine, Tag as TagIcon } from "lucide-react";
import { StarRating, IntentChips } from "@/components/Triage";
import { bumpWorth } from "@/lib/worth";
import { itemImage } from "@/lib/api";
import { toast } from "sonner";
import { BulkImport } from "@/components/BulkImport";

import { CLOTHING_SLOTS, SHOE_SLOTS, SHOE_TIP } from "@/lib/photos";
import { Footprints } from "lucide-react";

/** ID-scan style nudges under a photo slot: "Move closer", "Include the sleeves", "Looks dirty - wash first". Never blocks. */
const Guidance = ({ check, slot, hasDetail }) => {
  if (!check) return null;
  if (check.pending) return <p className="mt-1.5 text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid={`guidance-${slot}-pending`}><ScanLine className="h-3 w-3 animate-pulse" /> Checking the shot…</p>;
  const tips = [...(check.tips || [])];
  if (check.whole_garment_visible === false && !tips.some((t) => /sleeve|frame|whole/i.test(t))) tips.unshift("Include the whole garment - sleeves and hem");
  if (check.too_far && !tips.some((t) => /closer/i.test(t))) tips.unshift("Move closer");
  if (check.blurry) tips.push("Hold still - it's blurry");
  if (check.too_dark) tips.push("More light");
  if (slot === "tag" && check.tag_legible === false) tips.push("Tag not legible - you'll be asked for brand & size");
  const small = (check.small_elements || []).filter(Boolean);
  const dirty = !!check.looks_dirty;
  if (!tips.length && !small.length && !dirty) return <p className="mt-1.5 text-[11px] text-good inline-flex items-center gap-1" data-testid={`guidance-${slot}-ok`}><Check className="h-3 w-3" /> Good shot</p>;
  return (
    <div className="mt-1.5 space-y-1" data-testid={`guidance-${slot}`}>
      {tips.slice(0, 3).map((t, i) => <p key={i} className="text-[11px] inline-flex items-center gap-1 rounded-full bg-gray-25 border border-border px-2 h-6 mr-1"><ScanLine className="h-3 w-3" /> {t}</p>)}
      {small.length > 0 && slot === "front" && !hasDetail && <p className="text-[11px] text-muted-foreground">Spotted {small.join(", ")} - add a <b>Detail</b> shot so it's preserved.</p>}
      {dirty && <p className="text-[11px] text-gold-deep inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Looks like it needs a wash - we'll remind you to re-photo after.</p>}
    </div>
  );
};

export default function AddItem() {
  const navigate = useNavigate();
  const [images, setImages] = useState({});
  const [kind, setKind] = useState("clothing"); // clothing | shoes - decides the photo slots (Task 3)
  const SLOTS = kind === "shoes" ? SHOE_SLOTS : CLOTHING_SLOTS;
  const [enhance, setEnhance] = useState(true);
  const [step, setStep] = useState("upload"); // upload | processing | review
  const [progress, setProgress] = useState(0);
  const [item, setItem] = useState(null);
  const [checks, setChecks] = useState({});   // slot -> photo-check result (ID-scan style guidance)
  const [triage, setTriage] = useState({ stars: null, intent: null });

  const onFile = async (key, file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setImages((p) => ({ ...p, [key]: b64 }));
    // "snap, go": guidance never blocks - it runs in the background and only shows tips if something's off
    setChecks((c) => ({ ...c, [key]: { pending: true } }));
    api.photoCheck(b64, key).then((r) => setChecks((c) => ({ ...c, [key]: r }))).catch(() => setChecks((c) => ({ ...c, [key]: null })));
  };

  const process = async () => {
    if (!images.front) { toast.error("A front photo is required"); return; }
    setStep("processing");
    setProgress(15);
    const timer = setInterval(() => setProgress((p) => Math.min(p + 7, 90)), 1500);
    try {
      const created = await api.createItem({
        front_image: images.front,
        back_image: images.back || null,
        tag_image: images.tag || null,
        detail_image: images.detail || null,
        side_image: images.side || null,
        sole_image: images.sole || null,
        enhance,
      });
      clearInterval(timer);
      setProgress(100);
      setItem(created);
      setStep("review");
      bumpWorth();
    } catch (e) {
      clearInterval(timer);
      toast.error(e?.response?.data?.detail || "AI processing failed");
      setStep("upload");
    }
  };

  const save = async () => {
    try {
      await api.updateItem(item.id, {
        name: item.name, category: item.category, subtype: item.subtype,
        primary_color: item.primary_color, material: item.material,
        seasons: item.seasons, formality: item.formality, warmth_level: item.warmth_level,
        brand: item.brand, size: item.size,
        purchase_price: item.purchase_price ? Number(item.purchase_price) : null,
        purchase_date: item.purchase_date || null,
        estimated_retail_price: item.estimated_retail_price ? Number(item.estimated_retail_price) : null,
        worth_override: item.worth_override ? Number(item.worth_override) : null,
        wear_likelihood: triage.stars ? Math.max(1, triage.stars) : null,
        intent: triage.intent || (triage.stars === 0 ? "sell" : null),
      });
      bumpWorth();
      toast.success(triage.intent && ["sell", "too_small", "dont_like"].includes(triage.intent) ? "Added - and dropped in your sell pile" : "Added to your closet");
      navigate("/inventory");
    } catch (e) {
      toast.error("Could not save item");
    }
  };

  const set = (k, v) => setItem((p) => ({ ...p, [k]: v }));
  const toggleSeason = (s) => {
    const arr = item.seasons || [];
    set("seasons", arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s]);
  };

  if (step === "processing") {
    return (
      <div className="max-w-md mx-auto py-16 text-center" data-testid="ai-processing">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary">
          <Loader2 className="h-7 w-7 animate-spin text-foreground" />
        </div>
        <h2 className="font-display text-2xl mt-5">Styling your item…</h2>
        <p className="text-sm text-muted-foreground mt-2">Analyzing fabric & color, then pressing out the wrinkles.</p>
        <Progress value={progress} className="mt-6" data-testid="ai-processing-progress" />
        <div className="mt-4 space-y-1.5 text-sm text-left mx-auto w-fit">
          <p className={progress > 10 ? "" : "text-muted-foreground"}>✓ Reading the garment</p>
          <p className={progress > 40 ? "" : "text-muted-foreground"}>✓ Extracting details</p>
          <p className={progress > 70 ? "" : "text-muted-foreground"}>✓ Enhancing the photo</p>
        </div>
      </div>
    );
  }

  if (step === "review" && item) {
    return (
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-3xl tracking-[-0.02em]">Review & refine</h1>
        <p className="text-muted-foreground mt-1">We filled in the details. Tweak anything, add value info, then save.</p>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div>
            <div className="aspect-[3/4] rounded-[var(--radius)] overflow-hidden bg-secondary ring-1 ring-black/5">
              <img src={api.getItem && `${process.env.REACT_APP_BACKEND_URL}/api/items/${item.id}/image/${item.has_enhanced ? "enhanced" : "front"}`} alt="item" className="h-full w-full object-cover" data-testid="review-image" />
            </div>
            {item.has_enhanced && (
              <Badge className="mt-3" variant="secondary"><Sparkles className="h-3 w-3 mr-1" /> AI enhanced photo</Badge>
            )}
            {item.review_status === "possible_duplicate" && (
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-gray-25 px-3 py-2 text-xs" data-testid="review-duplicate-flag">
                <AlertTriangle className="h-4 w-4 text-foreground shrink-0" /> This looks like something already in your closet. It's flagged for review.
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div className="rounded-xl bg-secondary/60 p-4 grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-muted-foreground text-xs">Est. price when new</div><div className="font-mono-plex font-semibold" data-testid="review-retail-value">{money(item.estimated_retail_price)}</div></div>
              <div><div className="text-muted-foreground text-xs">Worth today</div><div className="font-mono-plex font-semibold text-foreground">{money(item.worth_override ?? item.estimated_worth)}</div></div>
              {item.retail_price_note && <p className="col-span-2 text-xs text-muted-foreground">{item.retail_price_note}</p>}
            </div>
            {(!images.tag || !item.brand || !item.size) && (
              <div className="rounded-xl border border-border bg-gray-25 px-3 py-2.5 text-xs flex items-start gap-2" data-testid="review-tag-prompt">
                <TagIcon className="h-4 w-4 shrink-0 mt-0.5" />
                <p>{!images.tag ? "No tag photo - " : "Couldn't read the tag fully - "}please fill in <b>{[!item.brand && "brand", !item.size && "size", !item.material && "material"].filter(Boolean).join(", ") || "anything the tag says"}</b> below so sizing, matches and listings are right.</p>
              </div>
            )}
            <div className="rounded-xl border border-border p-3.5 space-y-2.5" data-testid="review-triage">
              <p className="text-xs text-muted-foreground">How likely are you to wear it? <span className="opacity-70">(zero stars = straight to the sell pile)</span></p>
              <StarRating value={triage.stars} onChange={(s) => setTriage((t) => ({ ...t, stars: s, intent: s === 0 ? "sell" : t.intent }))} testid="add-stars" />
              <p className="text-xs text-muted-foreground pt-1">Keep or sell?</p>
              <IntentChips compact value={triage.intent} onChange={(i) => setTriage((t) => ({ ...t, intent: i }))} testid="add-intent" />
            </div>
            <Field label="Name"><Input value={item.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="review-name-input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <Select value={item.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="review-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Color"><Input value={item.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand"><Input value={item.brand || ""} onChange={(e) => set("brand", e.target.value)} data-testid="review-brand-input" /></Field>
              <Field label="Size"><Input value={item.size || ""} onChange={(e) => set("size", e.target.value)} /></Field>
            </div>
            <Field label="Formality">
              <Select value={item.formality || undefined} onValueChange={(v) => set("formality", v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{FORMALITIES.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Seasons">
              <div className="flex flex-wrap gap-2">
                {SEASONS.map((s) => (
                  <button key={s} onClick={() => toggleSeason(s)} className={`rounded-full px-3 py-1 text-xs capitalize border ${(item.seasons || []).includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{s}</button>
                ))}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Purchase price ($)"><Input type="number" value={item.purchase_price || ""} onChange={(e) => set("purchase_price", e.target.value)} data-testid="review-price-input" /></Field>
              <Field label="Est. new price ($)"><Input type="number" value={item.estimated_retail_price || ""} onChange={(e) => set("estimated_retail_price", e.target.value)} data-testid="review-retail-input" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Purchase date"><Input type="date" value={item.purchase_date ? item.purchase_date.slice(0,10) : ""} onChange={(e) => set("purchase_date", e.target.value)} data-testid="review-date-input" /></Field>
              <Field label="Worth override ($) — optional">
                <Input type="number" value={item.worth_override || ""} onChange={(e) => set("worth_override", e.target.value)} placeholder="Leave blank for estimate" />
              </Field>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={save} data-testid="add-item-save-button" className="rounded-full flex-1"><Check className="h-4 w-4 mr-1.5" /> Save to closet</Button>
              <Button variant="secondary" onClick={() => navigate("/inventory")} className="rounded-full">Later</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <p className="eyebrow">Build your closet</p>
      <h1 className="font-display text-[34px] sm:text-[44px] leading-[1.02] mt-1">Add pieces, <em className="em">your way.</em></h1>
      <p className="text-muted-foreground mt-2">Snap the front, back and tag. Our AI reads the details and makes the photo look sharp - even if it's wrinkled.</p>

      <div className="mt-6 inline-flex rounded-full bg-secondary p-1" role="tablist" aria-label="What are you adding?" data-testid="add-item-kind">
        {[["clothing", "Clothing", Camera], ["shoes", "Shoes", Footprints]].map(([k, label, Icon]) => (
          <button key={k} role="tab" aria-selected={kind === k} onClick={() => { setKind(k); setImages((p) => ({ front: p.front })); setChecks({}); }} className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium transition-colors ${kind === k ? "bg-background shadow-soft" : "text-muted-foreground hover:text-foreground"}`} data-testid={`add-item-kind-${k}`}><Icon className="h-4 w-4" /> {label}</button>
        ))}
      </div>
      {kind === "shoes" && <p className="mt-3 text-sm inline-flex items-center gap-2 rounded-xl bg-gray-25 border border-border px-3 py-2" data-testid="add-item-shoe-tip"><Footprints className="h-4 w-4" /> Tip: {SHOE_TIP}</p>}

      <div className={`grid grid-cols-2 ${SLOTS.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-4"} gap-4 mt-6`}>
        {SLOTS.map((slot) => (
          <div key={slot.key}>
            <Label className="text-sm">{slot.label}{slot.required ? <span className="text-foreground"> *</span> : <span className="text-muted-foreground font-normal"> · optional</span>}</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5 min-h-[28px]">{slot.hint}</p>
            <label className="mt-2 block aspect-[3/4] rounded-[var(--radius)] border-2 border-dashed border-border bg-card hover:border-foreground/40 transition-colors cursor-pointer overflow-hidden relative" data-testid={`add-item-${slot.key}-upload`}>
              {images[slot.key] ? (
                <>
                  <img src={images[slot.key]} alt={slot.label} className="h-full w-full object-cover" />
                  <button onClick={(e) => { e.preventDefault(); setImages((p) => { const n = { ...p }; delete n[slot.key]; return n; }); }} className="absolute top-2 right-2 rounded-full bg-background/90 p-1"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
                  <Camera className="h-7 w-7" />
                  <span className="text-xs mt-2">Tap to upload</span>
                </div>
              )}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(slot.key, e.target.files?.[0])} />
            </label>
            <Guidance check={checks[slot.key]} slot={slot.key} hasDetail={!!images.detail} />
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between rounded-[var(--radius)] border border-border bg-card p-4">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-foreground" />
          <div>
            <p className="text-sm font-medium">AI photo enhancement</p>
            <p className="text-xs text-muted-foreground">De-wrinkle & studio-style the front photo</p>
          </div>
        </div>
        <Switch checked={enhance} onCheckedChange={setEnhance} data-testid="add-item-ai-enhance-toggle" />
      </div>

      <Button onClick={process} className="mt-6 w-full rounded-full" size="lg" data-testid="add-item-analyze-button">
        <Sparkles className="h-4 w-4 mr-2" /> Analyze & Enhance
      </Button>

      <div className="flex items-center gap-4 my-8">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">or from a photo of you</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <FitPicMagic />

      <div className="flex items-center gap-4 my-8">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">or add many at once</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <BulkImport />
    </div>
  );
}

/** Fit Pic Magic - upload one outfit photo, get every piece as its own flat-lay in your closet. */
const FitPicMagic = () => {
  const navigate = useNavigate();
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [tick, setTick] = useState(0);
  const run = async () => {
    if (!img) return;
    setBusy(true); setResult(null); setTick(0);
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    try { setResult(await api.fromFitPic(img, false)); toast.success("Pieces added to your closet"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't read that photo"); }
    finally { clearInterval(t); setBusy(false); }
  };
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="fitpic-magic">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-foreground text-background"><Scan className="h-5 w-5" /></span>
        <div><p className="font-semibold">Import from a fit pic</p><p className="text-sm text-muted-foreground mt-0.5">Upload a photo of yourself in an outfit. We pull out every piece as its own studio flat-lay and catalog it.</p></div>
      </div>
      <div className="mt-4 grid sm:grid-cols-[180px_1fr] gap-4">
        <label className="aspect-[3/4] rounded-2xl border-2 border-dashed border-border bg-gray-25 hover:border-foreground/40 transition-colors cursor-pointer overflow-hidden relative" data-testid="fitpic-upload">
          {img ? <img src={img} alt="fit pic" className="h-full w-full object-cover" /> : <div className="h-full flex flex-col items-center justify-center text-muted-foreground"><Camera className="h-6 w-6" /><span className="text-xs mt-2">Your fit pic</span></div>}
          <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setImg(await fileToBase64(f)); setResult(null); } }} />
        </label>
        <div>
          {busy ? (
            <div className="rounded-xl bg-gray-25 border border-border p-4" data-testid="fitpic-processing">
              <p className="text-sm font-medium inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Pulling the pieces out… {tick}s</p>
              <p className="text-xs text-muted-foreground mt-1.5">Spotting each garment, rendering it alone on white, reading brand, colour, size and value. Usually 60-120 seconds.</p>
              <div className="mt-3 grid grid-cols-4 gap-2">{[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[3/4] rounded-lg bg-gray-100 animate-pulse" />)}</div>
            </div>
          ) : result ? (
            <div data-testid="fitpic-result">
              <p className="text-sm font-medium">{result.created.length} piece{result.created.length === 1 ? "" : "s"} added{result.failed?.length ? ` · ${result.failed.length} couldn't be isolated` : ""}</p>
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
                {result.created.map((it) => <div key={it.id} className="rounded-xl bg-gray-50 border border-border overflow-hidden" data-testid="fitpic-created-item"><div className="aspect-[3/4]"><img src={itemImage(it)} alt={it.name} className="h-full w-full object-cover mix-blend-multiply" /></div><p className="text-[11px] p-1.5 truncate font-medium">{it.name}</p></div>)}
              </div>
              <div className="mt-3 flex gap-2"><Button size="sm" onClick={() => navigate("/inventory")} className="rounded-full" data-testid="fitpic-open-closet">Open closet <ArrowRight className="h-4 w-4 ml-1" /></Button><Button size="sm" variant="ghost" onClick={() => { setImg(null); setResult(null); }} className="rounded-full">Another photo</Button></div>
            </div>
          ) : (
            <div className="h-full flex flex-col justify-between gap-3">
              <ul className="text-sm text-muted-foreground space-y-1.5"><li>· Full-body shots work best, in decent light</li><li>· Each piece gets a clean white-background product photo</li><li>· You can edit names, sizes and prices after</li></ul>
              <Button onClick={run} disabled={!img} className="rounded-full w-full sm:w-auto" data-testid="fitpic-run-button"><Sparkles className="h-4 w-4 mr-1.5" /> Extract the pieces</Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
